import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Components
import { TeamsManagementTable } from '../../../../components/display/tables/formTables/managementTables/TeamsManagementTable';
import { AddTeamForm } from '../../../../components/forms/AddTeamForm';

// Types
import { TeamManagement } from '../../../../components/forms/AddTeamForm/types';
import { TeamManagementLoader, Team } from './types';

// Styles
import { Container, TeamManagementSection } from './styles';

// Constants
import { formTeamManagementNaming } from '../../../../components/forms/AddTeamForm/constants';

// GQL
import { client } from '../../../../gql/client';
import { CREATE_TEAM, DELETE_TEAM } from '../../../../gql/mutations/teams';

// Redux Actions
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

// Utils
import { showSuccessToast, showErrorToast } from '../../../../utils/toasts';

// Sentry
import * as Sentry from '@sentry/react';

export default function TeamsManagementPage(): JSX.Element {
    const loaderData = useAsyncValue() as TeamManagementLoader;
    const revalidator = useRevalidator();
    const dispatch = useDispatch();

    const onReFetchData = useCallback(() => {
        revalidator.revalidate();
    }, [revalidator]);

    const onFormSubmit = async (data: TeamManagement) => {
        // Check name uniqueness
        if (loaderData.teams.some((team) => team.name === data.name)) {
            return 'El nombre del equipo ya existe';
        }

        await client.mutate({
            mutation: CREATE_TEAM,
            variables: {
                input: {
                    name: data.name.trim(),
                    concessionRelationships: data.concessionIDs || [],
                    roadRelationships: data.roadIDs || [],
                },
            },
        });

        onReFetchData();
    };

    const handleDeleteTeam = useCallback(async (teamId: string) => {
        try {
            await client.mutate({
                mutation: DELETE_TEAM,
                variables: { id: teamId },
            });
            showSuccessToast('Equipo eliminado correctamente');
            onReFetchData();
        } catch (err) {
            Sentry.captureException(err);
            console.log('Error deleting team:', err);
            showErrorToast('Error al eliminar el equipo');
        }
    }, [onReFetchData]);

    const onDeleteTeam = useCallback((teamId: string) => {
        const team = loaderData.teams.find((t) => t.id === teamId);
        if (!team) return;

        dispatch(
            openModal(modalIds.DELETE_TEAM_MODAL, {
                teamName: team.name,
                onConfirmDelete: async () => {
                    await handleDeleteTeam(teamId);
                },
            })
        );
    }, [dispatch, loaderData.teams, handleDeleteTeam]);

    const onEditTeam = useCallback((teamId: string, teamData: TeamManagement) => {
        const team = loaderData.teams.find((t) => t.id === teamId);
        if (!team) return;

        dispatch(
            openModal(modalIds.EDIT_TEAM_MODAL, {
                team: {
                    id: team.id,
                    name: team.name,
                    concessionIDs: team.concessionIDs || [],
                    roadIDs: team.roadIDs || [],
                    userIDs: team.userIDs || [],
                    vehicleIDs: team.vehicleIDs || [],
                },
                concessions: loaderData.cacheData.concessions,
                roads: loaderData.cacheData.roads,
                users: loaderData.cacheData.users,
                vehicles: loaderData.cacheData.vehicles,
                onSuccess: onReFetchData,
                onDelete: () => onDeleteTeam(teamId),
            })
        );
    }, [dispatch, loaderData.teams, loaderData.cacheData, onReFetchData, onDeleteTeam]);

    return (
        <Container>
            <TeamManagementSection>
                <AddTeamForm
                    initialData={loaderData.cacheData}
                    onFormSubmit={onFormSubmit}
                    namingPersistForm={formTeamManagementNaming.NEW_TEAM_MANAGEMENT_FORM}
                />
            </TeamManagementSection>
            <TeamManagementSection>
                <TeamsManagementTable
                    data={loaderData.teams}
                    onDeleteTeam={onDeleteTeam}
                    onEditTeam={onEditTeam}
                />
            </TeamManagementSection>
        </Container>
    );
}
